import React, { useEffect } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { inject, observer } from 'mobx-react';
import * as API from '../../lib/api';
import styled from 'styled-components';

import { Helmet } from 'react-helmet-async';

import SpinnerComponent from '../Common/Spinners';
import LeftSidebar from '../Common/LeftSidebar';
import PlayerMatchHistory from './MatchHistory';
import AgentStats from './AgentStats';
import StatsBySplit from './StatsBySplit';
import EditPlayerModal from './EditPlayerModal';
import MatchList from '../Common/MatchList';

import ReactCountryFlag from 'react-country-flag';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faLock } from '@fortawesome/free-solid-svg-icons';

const Wrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 20em;
  max-width: 75em;
`;

const BioContainer = styled.div`
  width: 100%;
  height: 7.5em;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  margin-bottom: .1em;
  ${props => props.$isMobile && 'padding-left: .5em;'};
  font-size: 0.85em;
  color: white;
  background: #666;
  overflow: hidden;
`

const PhotoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: ${props => props.$isMobile ? '100px' : '150px'};
  ${props => props.$isMobile && 'margin-left: -15px;'};
`

const Photo = styled.img`
  height: 100%;

  @media (max-width: 768px) {
    /* Mobile CSS changes go here */
    min-width: auto;
  }
`

const Info = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding-left: 0.5em;
`

const Nickname = styled.div`
  margin-top: 1em;
  font-size: 1.5em;
  font-weight: bold;

  > svg {
    margin-left: 1em;
    font-size: .65em;
    cursor: pointer;

    &:hover {
      color: #228fd8;
    }
  }
`

const InfoLowerContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
`

const InfoLeft = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  margin-right: .5em;
`

const RealName = styled.div`
  @media (max-width: 768px) {
    /* Mobile CSS changes go here */
  }
`

const Age = styled.div`

`

const InfoMiddle = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: 0 .5em;

  @media (max-width: 768px) {
    /* Mobile CSS changes go here */
    display: none;
  }
`

const Role = styled.div``

const ContactInfo = styled.div`
  max-width: 12em;
`

const MainAgents = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`

const AgentImage = styled.img`
  max-height: 2.5em;
  padding: 0 .25em;

  @media (max-width: 768px) {
    /* Mobile CSS changes go here */
    max-height: 1.5em;
  }
`

const InfoRight = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-left: .5em;

  @media (max-width: 768px) {
    /* Mobile CSS changes go here */
    display: none;
  }
`

const ContractEnd = styled.div``

const TeamLogoContainer = styled.div`
  height: 5.5em;
  width: 5.5em;
  padding: 1em;
`

const TeamLogo = styled.img`
  max-height: 100%;
  max-width: 100%;
`

const TabNav = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  margin: 3px 0 5px 0;
  background: #ccc;
  border-top: 1px solid #ccc;
  border-bottom: 1px solid #ccc;
  font-size: 0.85em;

  @media (max-width: 768px) {
    overflow-x: scroll;
  }
`

const TabNavItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  max-width: ${props => props.$wide ? '10em' : '8em'};
  max-height: 100%;
  padding: 3px;
  border-right: 1px solid ${props => props.$active ? '#228fd8' : '#ccc'};
  color: ${props => props.$active ? 'white' : 'black'};
  background-color: ${props => props.$active ? '#228fd8' : 'white'};
  text-align: center;

  &:hover {
    color: white;
    background-color: #228fd8;
    border-right: 1px solid #228fd8;
  }

  @media (min-width: 768px) {
    ${props => props.$specialTab === 'notes' && 'display: none;'}
  }
`

const ProfileContentAreaWrapper = styled.div`
  display: flex;
  flex-direction: row;
  max-width: 75em;
  // border: 1px solid #ccc;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`

const ProfileMainContentWrapper = styled.div`
  flex: 3;
  min-height: 20em;
  padding: .5em;
  border: 1px solid #ccc;
`

const ProfileSecondaryContentWrapper = styled.div`
  flex: 1;
  padding: .5em;
  border: 1px solid #ccc;
  border-left: none;
`

const MatchListContainer = styled.div`
  margin-bottom: 0.5em;
  border-bottom: 1px solid black;
`

const MatchListTitle = styled.div`
  font-size: 1.2em;
  font-weight: bold;
  margin-bottom: .1em;
  padding-bottom: 3px;
  border-bottom: 1px solid black;
`

const SectionLocked = styled.div`
  margin-top: 2em;
  font-size: 3em;
  text-align: center;
`
// TODO: Move this locking logic into the Notes component itself

const PlayerProfile = ({ AppStore }) => {
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  )
    ? true
    : false;

  const params = useParams();

  const route = 'players';
  const didRouteChange = route !== AppStore.currentRoute.get();

  const playerId = params.playerId;
  const playerProfileInView = AppStore.playerProfileInView.get();
  const didPlayerChange = playerId !== playerProfileInView;

  const playerFetching = AppStore.playerProfileFetching.get();
  const playerFetched = Boolean(playerId in AppStore.playerProfileDataMap);
  const playerMatchHistoryFetching = AppStore.playerMatchHistoryFetching.get();
  const playerStatsFetching = AppStore.playerStatsFetching.get();

  const playerInfo = AppStore.playerProfileDataMap[playerId];
  const agentsPlayed = playerInfo && JSON.parse(playerInfo.agents);
  const playerMatchHistoryData = AppStore.playerProfileDataMap[playerId]?.matchHistory;
  const notesAreBeingWorkedOn = AppStore.notesAreBeingWorkedOn.get();

  const playerUpcomingMatches = AppStore.upcomingMatchesMap[playerId];
  const upcomingMatchesFetching = AppStore.upcomingMatchesFetching.get();

  const playerRecentMatches = AppStore.recentMatchesMap[playerId];
  const recentMatchesFetching = AppStore.recentMatchesFetching.get();

  const usablePhoto = playerInfo?.imageUrl?.indexOf('transparent') !== -1 ? false : true;
  const defaultPhoto = playerInfo?.imageUrl?.indexOf('default') !== -1 ? true : false;

  const editPlayerModalShowing = AppStore.editPlayerModalShowing.get();

  const userGroupMemberships = AppStore.userGroupMemberships;
  /* const userHasNotesPermissions = PermittedRoles.filter(role => {
    return userGroupMemberships.includes(role);
  }).length > 0; */

  const pageTitle = `Oracle's Elixir Valorant - ${playerInfo
    ? playerInfo.nickname
    : 'Fetching'
    } Player Profile`;
  const pageDescription = `Player profile page${playerInfo && ' for ' + playerInfo.nickname
    }.`;

  const openEditPlayerModal = (event) => {
    event.preventDefault();
    AppStore.openCloseEditPlayerModal();
  }

  useEffect(() => {
    const fetchPlayerProfileData = (async (playerId) => {
      AppStore.setPlayerProfileFetching(true);
      const playerProfileData = await API.getPlayerProfile(playerId);
      AppStore.addPlayerProfileData(playerId, playerProfileData);
      AppStore.setPlayerProfileInView(playerId);
      AppStore.setPlayerProfileFetching(false);
    })

    const fetchPlayerMatchHistory = (async (playerId) => {
      AppStore.setPlayerMatchHistoryFetching(true);
      const matchHistoryData = await API.getPlayerMatchHistory(playerId);
      AppStore.addPlayerMatchHistoryData(playerId, matchHistoryData);
      AppStore.setPlayerMatchHistoryFetching(false);
    })

    if (!AppStore.playerProfileDataMap[playerId] && !playerFetching) {
      fetchPlayerProfileData(playerId);
    }

    if (playerFetched && !playerMatchHistoryData && !playerMatchHistoryFetching) {
      fetchPlayerMatchHistory(playerId);
    }

    if (!playerUpcomingMatches && !upcomingMatchesFetching) {
      AppStore.fetchUpcomingMatches({ type: 'player', id: playerId });
    }

    if (!playerRecentMatches && !recentMatchesFetching) {
      AppStore.fetchRecentMatches({ type: 'player', id: playerId });
    }

    if (didPlayerChange || didRouteChange) {
      AppStore.setPlayerProfileInView(playerId);
    }

    if (didRouteChange) {
      AppStore.setCurrentRoute(route);
    }
  }, ([
    AppStore,
    didPlayerChange,
    didRouteChange,
    playerId,
    playerInfo,
    playerProfileInView,
    playerFetched,
    playerFetching,
    playerMatchHistoryFetching,
    playerMatchHistoryData,
    playerStatsFetching,
    notesAreBeingWorkedOn,
    playerUpcomingMatches,
    upcomingMatchesFetching,
    playerRecentMatches,
    recentMatchesFetching
  ]))

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
      </Helmet>
      <LeftSidebar />
      <Wrapper className="mainContent">
        {playerFetching ? (
          <SpinnerComponent />
        ) : (
          playerFetched && (
            (!playerInfo) ? (
              <Navigate to={`/404?player/${playerId}`} replace />
            ) : (
              <>
                <EditPlayerModal showing={editPlayerModalShowing} playerId={playerId} playerInfo={playerInfo} />
                <BioContainer $isMobile={isMobile}>
                  <PhotoContainer $isMobile={isMobile}>
                    <Photo src={(usablePhoto && !defaultPhoto) ? playerInfo.imageUrl : 'https://static.lolesports.com/players/download.png'} />
                  </PhotoContainer>
                  <Info>
                    <Nickname>
                      {playerInfo.nickname}
                      {process.env.REACT_APP_ENVIRONMENT === "local" && (
                        <FontAwesomeIcon
                          icon={faPencil}
                          title='Edit player info'
                          onClick={openEditPlayerModal}
                        />
                      )}
                    </Nickname>
                    <InfoLowerContainer>
                      <InfoLeft>
                        <RealName>
                          {playerInfo.nationality && <ReactCountryFlag countryCode={playerInfo.nationality} svg />} {playerInfo.firstName} {playerInfo.lastName}
                        </RealName>
                        <Age>Age: {playerInfo.age || '?'}</Age>
                        {playerInfo.role && (<Role>{playerInfo.inGameLeader ? 'IGL, ' : null}{playerInfo.role}</Role>)}
                        {/* playerInfo.contactInfo && <ContactInfo>{playerInfo.contactInfo}</ContactInfo> */}
                      </InfoLeft>
                      <InfoMiddle>
                        <MainAgents>
                          {agentsPlayed ? (
                            agentsPlayed.map((agent, idx) => {
                              if (idx > 2) {
                                return null;
                              }
                              return (
                                <AgentImage
                                  key={`${playerId}-${agent.agentId}`}
                                  src={`https://media.valorant-api.com/agents/${agent.agentId}/displayicon.png`}
                                />
                              )
                            })
                          ) : (
                            '-'
                          )}
                        </MainAgents>
                      </InfoMiddle>
                      {/* <InfoRight>
                        <ContractEnd>Contract Ends: {playerInfo.contractEnd || 'unknown'}</ContractEnd>
                        <ContractEnd>Salary (est.): {playerInfo.salary || 'unknown'}</ContractEnd>
                          </InfoRight> */}
                    </InfoLowerContainer>
                  </Info>
                  {playerInfo.teamLogo && (
                    <TeamLogoContainer>
                      <TeamLogo title={playerInfo.teamName} src={playerInfo.teamLogo} />
                    </TeamLogoContainer>
                  )}
                </BioContainer>
                <TabNav>
                  <Link to={`/player/${playerId}/matchHistory`}>
                    <TabNavItem $active={!params.tab || params.tab === 'matchHistory'}>Match History</TabNavItem>
                  </Link>
                  <Link to={`/player/${playerId}/agentStats`}>
                    <TabNavItem $active={params.tab === 'agentStats'}>Agent Stats</TabNavItem>
                  </Link>
                  <Link to={`/player/${playerId}/statsBySplit`}>
                    <TabNavItem $active={params.tab === 'statsBySplit'}>Stats by Split</TabNavItem>
                  </Link>
                  {/* isMobile && (
                    <Link to={`/player/${playerId}/notes`}>
                      <TabNavItem
                        $active={params.tab === 'notes'}
                        $specialTab='notes'
                      >
                        Notes
                      </TabNavItem>
                    </Link>
                  ) */}
                </TabNav>
                <ProfileContentAreaWrapper>
                  <ProfileMainContentWrapper>
                    {/*
                      params.tab === 'notes' ? (
                        userHasNotesPermissions ? (
                          <Notes
                            entityId={playerId}
                            entityType='player'
                            notes={playerInfo.notes}
                          />
                        ) : (
                          <SectionLocked>
                            <FontAwesomeIcon
                              icon={faLock}
                              title='Section locked'
                            />
                          </SectionLocked>
                        )
                      ) : (params.tab === 'matchHistory' || !params.tab) ? (
                        <PlayerMatchHistory fetchedYet={Boolean(playerMatchHistoryData)} fetching={playerMatchHistoryFetching} games={playerMatchHistoryData} />
                      ) : params.tab === 'agentStats' ? (
                        <AgentStats playerId={playerId} agentStats={agentStats} fetching={playerMatchHistoryFetching || playerStatsFetching} />
                      ) : (
                        <Navigate to='/404' replace />
                      )
                    */}
                    {
                      (params.tab === 'matchHistory' || !params.tab) ? (
                        <PlayerMatchHistory fetchedYet={Boolean(playerMatchHistoryData)} fetching={playerMatchHistoryFetching} games={playerMatchHistoryData} />
                      ) : params.tab === 'agentStats' ? (
                        <AgentStats playerId={playerId} />
                      ) : params.tab === 'statsBySplit' ? (
                        <StatsBySplit playerId={playerId} />
                      ) : (
                        <Navigate to='/404' replace />
                      )
                    }
                  </ProfileMainContentWrapper>
                  <ProfileSecondaryContentWrapper>
                    <MatchListContainer>
                    <MatchListTitle>
                      Upcoming Matches
                    </MatchListTitle>
                    <MatchList
                      view='player'
                      type='upcoming'
                      matchList={playerUpcomingMatches}
                      fetching={upcomingMatchesFetching}
                    />
                    </MatchListContainer>
                    <MatchListContainer>
                    <MatchListTitle>
                      Recent Matches
                    </MatchListTitle>
                    <MatchList
                      view='player'
                      type='recent'
                      matchList={playerRecentMatches}
                      fetching={recentMatchesFetching}
                    />
                    </MatchListContainer>
                  </ProfileSecondaryContentWrapper>
                  {/* <ProfileSecondaryContentWrapper>
                    {userHasNotesPermissions ? (
                      <Notes
                        entityId={playerId}
                        entityType='player'
                        notes={playerInfo.notes}
                      />
                    ) : (
                      <SectionLocked>
                        <FontAwesomeIcon
                          icon={faLock}
                          title='Section locked'
                        />
                      </SectionLocked>
                    )}
                    </ProfileSecondaryContentWrapper> */}
                </ProfileContentAreaWrapper>
              </>
            )
          )
        )}
      </Wrapper>
    </>
  );
};

export default inject("AppStore")(observer(PlayerProfile));
